import DOMPurify from 'dompurify';

/**
 * 공지 본문(HTML) sanitize.
 *
 * 본문은 운영자가 Quill 에디터로 작성한 HTML 이다
 * (naegift-operator/src/pages/notices/NoticeManage.jsx:28 QUILL_FORMATS).
 * 화이트리스트는 그 formats 목록 = header(h1~h3)·bold·italic·underline·color·list·link 의
 * quill 1.3.7 출력 태그와 일치시킨다. 그 밖의 태그(img/table/script/iframe 등)는 전부 제거된다.
 *
 * ★DB 에 직접 들어간 본문까지 신뢰하지 않는다 — 렌더 직전에 항상 이 함수를 거친다.
 */
const ALLOWED_TAGS = [
  'p',
  'br',
  'span',
  'strong',
  'em',
  'u',
  's',
  'h1',
  'h2',
  'h3',
  'ol',
  'ul',
  'li',
  'a',
  'blockquote',
];

/**
 * DOMPurify 의 ALLOWED_ATTR 은 태그별 지정이 안 된다 — 여기서 크게 열고
 * afterSanitizeAttributes 훅에서 태그별로 좁힌다(style 은 span 의 color 만, class 는 ql-* 만).
 */
const ALLOWED_ATTR = ['href', 'target', 'rel', 'style', 'class'];

/** quill 이 들여쓰기/정렬에 쓰는 클래스만 허용한다(ql-indent-1, ql-align-center …). */
const QUILL_CLASS_RE = /^ql-(?:indent|align)-[a-z0-9-]+$/i;

/**
 * 태그별 속성 좁히기 훅.
 *  · style   : span 의 color 만 남긴다(배경·폰트크기 등으로 레이아웃을 깨지 못하게).
 *  · class   : ql-indent-*, ql-align-* 만 남긴다(앱 CSS 클래스 사칭 방지).
 *  · a       : target=_blank + rel=noopener noreferrer 강제(탭 탈취 차단).
 *              href 스킴 검증(javascript:, data: 차단)은 DOMPurify 기본
 *              ALLOWED_URI_REGEXP 가 이미 처리하므로 여기서 다시 하지 않는다.
 */
function narrowAttributes(node: Element): void {
  if (typeof node?.getAttribute !== 'function') return;

  if (node.hasAttribute('style')) {
    const styled = node as HTMLElement;
    const color = node.tagName === 'SPAN' ? styled.style?.color || '' : '';
    node.removeAttribute('style');
    if (color) {
      styled.style.color = color;
    }
  }

  if (node.hasAttribute('class')) {
    const kept = Array.prototype.slice
      .call(node.classList)
      .filter((cls: string) => QUILL_CLASS_RE.test(cls));
    if (kept.length) {
      node.setAttribute('class', kept.join(' '));
    } else {
      node.removeAttribute('class');
    }
  }

  if (node.tagName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
}

/**
 * 훅은 DOMPurify 인스턴스 전역이라 등록해두면 앱의 다른 sanitize 까지 영향받는다.
 * 호출마다 add → sanitize → remove 로 감싸 이 함수 밖으로 새지 않게 한다
 * (sanitize 는 동기라 이 구간에 다른 호출이 끼어들지 않는다).
 */
export function sanitizeNoticeHtml(html?: string | null): string {
  if (!html) return '';
  try {
    DOMPurify.addHook('afterSanitizeAttributes', narrowAttributes);
    try {
      return DOMPurify.sanitize(html, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        ALLOW_DATA_ATTR: false,
        ALLOW_ARIA_ATTR: false,
      }) as string;
    } finally {
      DOMPurify.removeHook('afterSanitizeAttributes');
    }
  } catch (e) {
    // sanitize 실패 시 원문을 그대로 내보내지 않는다 — 빈 본문이 낫다.
    console.log(e);
    return '';
  }
}

export default sanitizeNoticeHtml;
